/** Shared types for Quest Jump. Kept free of Phaser imports so pure logic can use them. */

/**
 * The stage's shape, chosen once at startup (docs/PHONE_LAYOUT.md).
 *
 * Computers and landscape tablets get the 1280x720 stage everything was
 * designed for. A phone or tablet held upright gets a tall stage, 720 wide and
 * as tall as the screen's proportions ask, so it fills the screen; the device
 * keyboard then covers the bottom part, and screens keep what matters above it.
 *
 * Dev builds can force either with ?layout=portrait or ?layout=landscape.
 */
export type StageLayout = 'landscape' | 'portrait';

/** Phones and upright tablets get the tall stage. False puts every device on the wide one. */
export const PORTRAIT_FOR_DEVICES = true;

function detectLayout(): StageLayout {
  if (typeof window === 'undefined') return 'landscape';
  const asked = new URLSearchParams(window.location.search).get('layout');
  if (import.meta.env.DEV && (asked === 'portrait' || asked === 'landscape')) return asked;
  if (!PORTRAIT_FOR_DEVICES) return 'landscape';
  const touch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  return touch && window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

export const LAYOUT: StageLayout = detectLayout();
export const IS_PORTRAIT = LAYOUT === 'portrait';

/** The design width every level is laid out in; see stageX(). */
export const DESIGN_WIDTH = 1280;

export const GAME_WIDTH = IS_PORTRAIT ? 720 : 1280;
export const GAME_HEIGHT = IS_PORTRAIT
  ? // At least 1280: shorter screens (an upright iPad) get thin side bars
    // rather than a cramped layout.
    Math.min(1600, Math.max(1280, Math.round((720 * window.innerHeight) / window.innerWidth)))
  : 720;

/**
 * Where the device keyboard begins, as a stage y: everything a player needs
 * while typing stays above it. The whole stage on a computer.
 */
export const TYPING_AREA_BOTTOM = IS_PORTRAIT ? Math.round(GAME_HEIGHT * 0.56) : GAME_HEIGHT;

/** Tall stage, in a level: the word panel's centre, just above the keyboard. */
export const PORTRAIT_PANEL_Y = TYPING_AREA_BOTTOM - 110;
/** Tall stage, in a level: the top of the dock that holds the word. */
export const PORTRAIT_DOCK_TOP = PORTRAIT_PANEL_Y - 120;

export type SceneKey =
  | 'Boot'
  | 'Preload'
  | 'Title'
  | 'ProfileSelect'
  | 'LevelMap'
  | 'AnimalSelect'
  | 'Game'
  | 'Results'
  | 'Settings'
  | 'ParentStats'
  | 'TideStart'
  | 'TideSummary'
  | 'Helpers'
  | 'Grade';

/** Grade bands drive word difficulty and par WPM (see CLAUDE.md). */
export type GradeBand = 3 | 4 | 5 | 6 | 7 | 8;

export type AssetGroupName = 'animals' | 'platforms' | 'backgrounds' | 'powerups' | 'ui' | 'audio';

/** One manifest entry: the file plus the logical size it was authored at. */
export interface AssetEntry {
  file: string;
  width?: number;
  height?: number;
  frames?: number;
  /**
   * Short content hash, written by scripts/build-manifest.ts. The loader adds
   * it to the URL as ?v=, so art can be cached for a year by the host and a
   * changed file is still fetched fresh (docs/DEPLOY.md).
   */
  hash?: string;
}

export interface AssetManifest {
  version: number;
  note?: string;
  basePath: string;
  groups: Record<AssetGroupName, Record<string, AssetEntry>>;
}

/** Asset keys are always "<group>/<path>", e.g. "platforms/ice-floe/platform_1". */
export type AssetKey = string;

export type PlaceholderShape =
  | 'rect'
  | 'roundRect'
  | 'ellipse'
  | 'floe'
  | 'cloud'
  | 'capsule'
  | 'marshmallow'
  | 'soccerField'
  | 'gameDay'
  | 'star'
  | 'icon'
  | 'panel'
  | 'backdrop';

export interface PlaceholderSpec {
  width: number;
  height: number;
  shape: PlaceholderShape;
  /** Body colour. Placeholder animal bodies stay near-white so setTint reads true. */
  fill: number;
  accent: number;
  outline: number;
  outlineWidth: number;
}

/** Bundled Atkinson Hyperlegible, with system fallbacks (CLAUDE.md). */
export const FONT_FAMILY = '"Atkinson Hyperlegible", system-ui, sans-serif';

/** Shared UI palette for placeholder-era screens. */
export const PALETTE = {
  ink: '#1B2A41',
  snow: '#F7FBFF',
  ice: '#8ECAE6',
  sky: '#0B1220',
  sunset: '#FF7B54',
  teal: '#2EC4B6',
  gold: '#FFC145',
} as const;

/* ------------------------------------------------------------------- levels */

/** Platform 0 is the starting floe, the top platform is the goal. */
export type PlatformKind = 'normal' | 'spring' | 'jetpack' | 'start' | 'goal';

/** Difficulty class a word was drawn for: spring words are moderate, jetpacks hard. */
export type WordKind = 'normal' | 'moderate' | 'hard';

/** One environment per 10-level band (CLAUDE.md visual themes table). */
export type BackgroundTheme =
  | 'sunset'
  | 'midday-glacier'
  | 'snowstorm-dusk'
  | 'aurora-night'
  | 'iceberg-sea'
  | 'research-station'
  | 'ice-cave'
  | 'blizzard'
  | 'midnight-sun'
  | 'grand-aurora';

/** Concrete platform looks. 'mixed' means per-platform themes (levels 91-100). */
export type PlatformTheme =
  'ice-floe' | 'cloud' | 'marshmallow' | 'soccer-field' | 'game-day' | 'mixed';

/** Horizontal safe area for platform centres, so a 320px goal never clips the stage. */
export const SAFE_X_MIN = 200;
export const SAFE_X_MAX = 1080;
/** Levels are generated in design space, so a level is the same on every screen. */
export const STAGE_CENTER_X = DESIGN_WIDTH / 2;

/**
 * Design-space x (the 1280-wide stage levels are generated for) to stage x.
 * Identity on the wide stage; on the tall one the safe band is squeezed into
 * the narrower column so a 256px platform always fits on screen.
 */
export function stageX(designX: number): number {
  if (!IS_PORTRAIT) return designX;
  const half = GAME_WIDTH / 2 - 128 - 8;
  const designHalf = (SAFE_X_MAX - SAFE_X_MIN) / 2;
  return GAME_WIDTH / 2 + ((designX - STAGE_CENTER_X) / designHalf) * half;
}

export interface PlatformSpec {
  readonly index: number;
  /** Logical-pixel centre on the 1280-wide stage. Y spacing is the renderer's call. */
  readonly x: number;
  readonly kind: PlatformKind;
  /** The word typed while standing on the platform below. Absent only on 'start'. */
  readonly word?: string;
  readonly wordScore?: number;
  /** Platforms gained when a bonus word is cleared: 2-3 spring, 5-6 jetpack. */
  readonly bonusDistance?: number;
  /** Set only when the level's platformTheme is 'mixed'. */
  readonly theme?: PlatformTheme;
  /** Sentence levels: the sentence this platform's word belongs to. */
  readonly sentence?: SentenceSpot;
}

/** Where a word sits in its sentence, for showing the whole sentence while typing. */
export interface SentenceSpot {
  /** Every word of the sentence as shown: "Birds", "sing", "spring." */
  readonly words: readonly string[];
  /** This platform's word in it. */
  readonly index: number;
}

export interface LevelConfig {
  readonly level: number;
  readonly platformCount: number;
  /** The grade the level's words come from. */
  readonly gradeBand: GradeBand;
  readonly targetWpm: number;
  /** How far up that grade's word list the level sits, 0 to 1. */
  readonly wordProgress: number;
  readonly backgroundTheme: BackgroundTheme;
  readonly platformTheme: PlatformTheme;
  /** Levels 31-40 use glowing ice floes rather than a separate asset set. */
  readonly platformGlow: boolean;
  readonly platforms: readonly PlatformSpec[];
  readonly bonusCount: number;
  readonly parSeconds: number;
  /** The last level of each world: the climb is sentences, typed word by word. */
  readonly sentenceLevel: boolean;
  /**
   * Lenient on sentence levels in grades 3 to 5: capitals and the final full
   * stop are shown but not required. Strict everywhere else.
   */
  readonly match: 'strict' | 'lenient';
}

/* -------------------------------------------------------------- gameplay */

/** Per-position state of what the player has typed against the target word. */
export type LetterState = 'correct' | 'wrong' | 'pending';

/** Animal animation states (docs/ART_SPEC.md frame counts in brackets). */
export type AnimalState =
  | 'idle' // 2
  | 'crouch' // 1
  | 'jump' // 1
  | 'fall' // 1
  | 'land' // 1
  | 'cheer' // 2
  | 'oops'; // 1

/** What one play of a level produced. Handed to the Results scene. */
export interface GameRunStats {
  readonly level: number;
  readonly platformCount: number;
  readonly reachedGoal: boolean;
  readonly wordsAttempted: number;
  readonly wordsCorrect: number;
  /** Printable keys pressed, and how many matched the target at that position. */
  readonly keystrokes: number;
  readonly keystrokeHits: number;
  /** Keystroke accuracy, 0-100: the number shown on the HUD. */
  readonly accuracy: number;
  /** Whole-word accuracy, 0-100: feeds Prompt 6 scoring and Prompt 12 stats. */
  readonly wordAccuracy: number;
  readonly longestStreak: number;
  readonly elapsedMs: number;
  readonly score: number;
  /** 1-3 when the level was completed, 0 otherwise. */
  readonly stars: number;
  /** Net words per minute over the correctly typed words. */
  readonly wpm: number;
  readonly bonusWordsHit: number;
  readonly springsUsed: number;
  readonly jetpacksUsed: number;
  readonly bonusWordsMissed: number;
  readonly missedWords: readonly string[];
  readonly missedLetters: Readonly<Record<string, number>>;
  /** Every word that had a slip, in order: for the "What I missed" review on Results. */
  readonly wordSlips?: readonly WordSlip[];
}

/** One word that didn't go perfectly. */
export interface WordSlip {
  readonly word: string;
  /** What was submitted: the word itself when the slips were fixed before sending. */
  readonly typed: string;
  /** Submitted wrong (the animal dropped), rather than fixed on the way. */
  readonly missed: boolean;
  /** Wrong keys pressed while typing it. */
  readonly slips: number;
}
